<script setup>
  import { computed, onMounted, watch } from 'vue'
  import { useTemplateStore } from '@/stores/template'
  import { useActivity } from '@/composables/useActivity'
  import { useActivityStore } from '@/stores/activity'
  import { useAlert } from '@/composables/useAlert'
  import { createLogger } from '@/shared/logger'
  import PublinkerContextualHeader from '@/components/PublinkerContextualHeader.vue'
  import { useRoute } from 'vue-router'

  const { VITE_PUBLINKER_API_BASE, VITE_LEADCLUMP_API_BASE, MODE } = import.meta
    .env

  const logger = createLogger('BaseLayout')
  const alertService = useAlert()
  const route = useRoute()

  // Import all layout partials
  import BaseHeader from '@/layouts/partials/BaseHeader.vue'
  import BaseSidebar from '@/layouts/partials/BaseSidebar.vue'
  import BaseFooter from '@/layouts/partials/BaseFooter.vue'

  // Component properties
  defineProps({
    sidebarWithMiniNav: {
      type: Boolean,
      default: false,
      description: 'If the sidebar is in Mini Nav Mode'
    }
  })

  // Main store
  const store = useTemplateStore()
  const activityStore = useActivityStore()

  // Computed to check if the current route is the landing page
  const isLandingPage = computed(() => {
    return route.name === 'landing' || route.path.includes('/landing')
  })

  // Computed to determine whether to show the RouterView
  const shouldShowRouterView = computed(() => {
    return isLandingPage.value || store.siteDataLoaded
  })

  // Set default color theme
  store.setColorTheme({
    theme: store.settings.colorTheme
  })

  // Render main classes based on store options
  const classContainer = computed(() => {
    return {
      'sidebar-r': store.layout.sidebar && !store.settings.sidebarLeft,
      'sidebar-mini': store.layout.sidebar && store.settings.sidebarMini,
      'sidebar-o': store.layout.sidebar && store.settings.sidebarVisibleDesktop,
      'sidebar-o-xs':
        store.layout.sidebar && store.settings.sidebarVisibleMobile,
      'sidebar-dark':
        store.layout.sidebar &&
        store.settings.sidebarDark &&
        !store.settings.darkMode,
      'side-overlay-o':
        store.layout.sideOverlay && store.settings.sideOverlayVisible,
      'side-overlay-hover':
        store.layout.sideOverlay && store.settings.sideOverlayHoverable,
      'enable-page-overlay':
        store.layout.sideOverlay && store.settings.pageOverlay,
      'page-header-fixed': store.layout.header && store.settings.headerFixed,
      'page-header-dark':
        store.layout.header &&
        store.settings.headerDark &&
        !store.settings.darkMode,
      'main-content-boxed': store.settings.mainContent === 'boxed',
      'main-content-narrow': store.settings.mainContent === 'narrow',
      'rtl-support': store.settings.rtlSupport,
      'side-trans-enabled': store.settings.sideTransitions,
      'side-scroll': true,
      'sidebar-dark page-header-dark dark-mode': store.settings.darkMode
    }
  })

  // Aplicar dark-mode também no html e body para máxima cobertura
  const applyDarkModeToDocument = () => {
    if (store.settings.darkMode) {
      document.documentElement.classList.add('dark-mode')
      document.body.classList.add('dark-mode')
    } else {
      document.documentElement.classList.remove('dark-mode')
      document.body.classList.remove('dark-mode')
    }
  }

  // Aplicar inicialmente
  applyDarkModeToDocument()

  // Watch para mudanças
  watch(
    () => store.settings.darkMode,
    () => {
      applyDarkModeToDocument()
    }
  )

  // Change dark mode based on dark mode system preference
  if (store.settings.darkModeSystem) {
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      store.darkMode({ mode: 'on' })
    } else {
      store.darkMode({ mode: 'off' })
    }
  }

  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => {
      if (store.settings.darkModeSystem) {
        if (e.matches) {
          store.darkMode({ mode: 'on' })
        } else {
          store.darkMode({ mode: 'off' })
        }
      }
    })

  // Remove side transitions on window resizing
  onMounted(() => {
    let winResize = false

    window.addEventListener('resize', () => {
      clearTimeout(winResize)

      store.setSideTransitions({ transitions: false })

      winResize = setTimeout(() => {
        store.setSideTransitions({ transitions: true })
      }, 500)
    })

    // Verification of session status in relation to inactivity
    logger.group('🔄 Session Check')
    logger.log(
      '✨ Status:',
      'Checking if user was logged out due to inactivity'
    )

    // Execution of previous inactivity check
    const wasLoggedOut = activityStore.checkIfLoggedOutDueToInactivity()
    logger.log(
      '📊 Result:',
      wasLoggedOut ? 'User was logged out' : 'Normal session'
    )
    logger.groupEnd()

    if (wasLoggedOut) {
      logger.group('🚨 Session Timeout')
      logger.log('✨ Status:', 'User logged back in after inactivity timeout')
      logger.log('🔄 Action:', 'Showing toast notification')
      logger.groupEnd()

      // Display of informative notification about session refresh
      alertService.showToast(
        'Welcome back! Your session was refreshed due to inactivity.',
        'info',
        {
          timer: 10000 // 10 seconds to ensure message visibility
        }
      )
    }
  })

  // Emits
  const emit = defineEmits(['setSite'])

  const setSite = site => {
    emit('setSite', site)
  }

  // Initialization of activity monitoring system
  useActivity()

  // Filtra todas as variáveis VITE_ do env
  const envVars = computed(() => {
    return Object.entries(import.meta.env)
      .filter(([key]) => key.startsWith('VITE_'))
      .reduce((acc, [key, value]) => {
        // Exclui as APIs pois já são mostradas separadamente
        if (
          key !== 'VITE_PUBLINKER_API_BASE' &&
          key !== 'VITE_LEADCLUMP_API_BASE'
        ) {
          acc[key.replace('VITE_', '')] = value
        }
        return acc
      }, {})
  })
</script>

<template>
  <div id="page-container" :class="classContainer">
    <!-- Page Loader -->
    <div id="page-loader" :class="{ show: store.settings.pageLoader }"></div>

    <!-- Page Overlay -->
    <div
      v-if="store.layout.sideOverlay && store.settings.pageOverlay"
      id="page-overlay"
      @click="store.sideOverlay({ mode: 'close' })"
    ></div>
    <!-- END Page Overlay -->

    <!-- Publinker Contextual Header -->
    <PublinkerContextualHeader />
    <!-- END Publinker Contextual Header -->

    <!-- Sidebar -->
    <BaseSidebar
      v-if="store.layout.sidebar"
      :with-mini-nav="sidebarWithMiniNav"
    >
      <template #header>
        <slot name="sidebar-header"></slot>
      </template>

      <template #header-extra>
        <slot name="sidebar-header-extra"></slot>
      </template>

      <template #content>
        <slot name="sidebar-content"></slot>
      </template>

      <slot name="sidebar"></slot>
    </BaseSidebar>
    <!-- END Sidebar -->

    <!-- Header -->
    <BaseHeader
      v-if="store.layout.header"
      @set-site="setSite"
      :MODE="MODE"
      :envVars="envVars"
      :VITE_PUBLINKER_API_BASE="VITE_PUBLINKER_API_BASE"
      :VITE_LEADCLUMP_API_BASE="VITE_LEADCLUMP_API_BASE"
    >
      <template #content-left>
        <slot name="header-content-left"></slot>
      </template>

      <template #content-right>
        <slot name="header-content-right"></slot>
      </template>

      <template #content>
        <slot name="header-content"></slot>
      </template>
      <slot name="header"></slot>
    </BaseHeader>
    <!-- END Header -->

    <!-- Main Container -->
    <div id="main-container">
      <slot name="page-top-content"></slot>
      <RouterView
        v-if="shouldShowRouterView"
        :key="store.site + $route.fullPath"
      />
    </div>
    <!-- END Main Container -->

    <!-- Footer -->
    <BaseFooter v-if="store.layout.footer">
      <template #content-left>
        <slot name="footer-content-left"></slot>
      </template>

      <template #content-right>
        <slot name="footer-content-right"></slot>
      </template>
      <slot name="footer"></slot>
    </BaseFooter>
    <!-- END Footer -->
  </div>
</template>

<style lang="scss">
  // Responsividade
  @media (max-width: 992px) {
    .environment-indicator {
      width: calc(100% - 2rem);

      .api-info {
        .api-url {
          white-space: normal;
          word-break: break-all;
        }
      }
    }
  }
</style>
